import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";

export type ProceedingStatus = "draft" | "published" | "awarded" | "cancelled";
export type AccessMode = "open" | "invited" | "qualified";

export const ACCESS_MODES: { value: AccessMode; label: string; hint: string }[] = [
  {
    value: "open",
    label: "W pełni jawne",
    hint: "Widzi każdy podwykonawca z dostępem do postępowań.",
  },
  {
    value: "invited",
    label: "Dla wskazanych firm",
    hint: "Widzą wyłącznie firmy wybrane z listy — postępowanie nie trafi na listę publiczną.",
  },
  {
    value: "qualified",
    label: "Dla prekwalifikowanych",
    hint: "Widzą firmy pokrywające WSZYSTKIE wskazane pozycje i (jeśli podane) województwo.",
  },
];

export interface ProceedingAttachment {
  id: string;
  label: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status: ScanStatus;
  created_at: string;
  url: string | null;
}

export interface Proceeding {
  id: string;
  number: string;
  title: string;
  description: string;
  responsible: string | null;
  responsible_name: string | null;
  responsible_phone: string;
  responsible_email: string;
  project: string | null;
  project_name: string | null;
  submission_deadline: string;
  delivery_deadline: string | null;
  max_price_net: string | null;
  max_price_visible: boolean;
  /** Jednostka wyceny pokazywana oferentowi (np. „za komplet 100 działek”). */
  price_basis: string;
  declaration_questions: string[];
  access_mode: AccessMode;
  access_mode_label: string;
  positions: string[];
  positions_detail: { id: string; name: string; category_name: string }[];
  voivodeship: string;
  voivodeship_label: string;
  /** Powiaty realizacji (kody TERYT) — pusta lista = całe województwo. */
  counties: string[];
  county_labels: string[];
  invited_contractors: string[];
  invited_detail: { id: string; name: string }[];
  /** Wykluczone z TEGO postępowania — nie blokada firmy, patrz backend. */
  excluded_contractors: string[];
  excluded_detail: { id: string; name: string }[];
  /** Ogłoszenie widoczne na publicznej stronie (tylko tryb w pełni jawny). */
  is_public: boolean;
  /** Tura się skończyła, a wynik nie zapadł — kupiec ma coś do zrobienia. */
  awaits_resolution: boolean;
  attachments: ProceedingAttachment[];
  status: ProceedingStatus;
  status_label: string;
  published_at: string | null;
  is_open_for_offers: boolean;
  winning_offer: string | null;
  winning_contractor_name: string | null;
  winning_amount_net: string | null;
  cancel_reason: string;
  cancel_reason_label: string;
  resolution_note: string;
  resolved_at: string | null;
  resolved_by_name: string | null;
  generated_order: string | null;
  generated_order_number: string | null;
  /** Ustawione, gdy ogłoszenie prowadzi etap projektowy DLC_01 (Projekt techniczny /
   *  Kosztorys / MDCP). Wtedy zamówienie tworzy się WYŁĄCZNIE w panelu etapu — tylko ta
   *  droga przechodzi przez zapotrzebowanie ze znacznikiem etapu, więc tylko po niej
   *  działają automaty DLC_01 (typ zamówienia, punkty MDCP, obrys dla geodety). */
  stage_link: {
    connection_id: string;
    /** Id Przedsięwzięcia (hub) — do linku /power-projects/:id (trasa bierze id huba). */
    project_id: string;
    path: "technical" | "cost_estimate" | "mdcp";
    path_label: string;
    link_kind: "primary" | "correction";
    frozen_at: string | null;
    ordered: boolean;
  } | null;
  /* Dogrywka — druga tura po otwarciu ofert. */
  runoff_started_at: string | null;
  runoff_deadline: string | null;
  runoff_note: string;
  runoff_started_by_name: string | null;
  max_price_revealed: boolean;
  is_runoff_open: boolean;
  has_runoff: boolean;
  created_at: string;
}

export const CANCEL_REASONS = [
  { value: "no_offers", label: "Brak ofert" },
  { value: "above_max", label: "Wszystkie oferty powyżej ceny maksymalnej" },
  { value: "resigned", label: "Rezygnacja z zakupu" },
  { value: "other", label: "Inny" },
] as const;

/** Ogłoszenie oczami oferenta — bez trybu dostępu, listy zaproszonych i ukrytej ceny. */
export interface PublicProceeding {
  id: string;
  number: string;
  title: string;
  description: string;
  responsible_name: string | null;
  responsible_phone: string;
  responsible_email: string;
  submission_deadline: string;
  delivery_deadline: string | null;
  max_price_net: string | null;
  /** Idzie do oferenta zawsze — także gdy kwota maksymalna jest ukryta. */
  price_basis: string;
  declaration_questions: string[];
  voivodeship: string;
  voivodeship_label: string;
  county_labels: string[];
  positions_detail: { id: string; name: string; category_name: string }[];
  attachments: ProceedingAttachment[];
  published_at: string | null;
  is_open_for_offers: boolean;
  status: ProceedingStatus;
  status_label: string;
  outcome: "won" | "lost" | "cancelled" | "awarded" | null;
  my_offer: {
    amount_net: string;
    submitted_at: string | null;
    status: string;
    status_label: string;
  } | null;
  /** Dogrywka oczami oferenta — null, dopóki nie została ogłoszona. */
  runoff: RunoffState | null;
  /** Zamrożony zakres MDCP (punkty + obrys na mapę OSM) — tylko postępowania MDCP po publikacji. */
  mdcp_scope: MdcpScope | null;
}

/** Zakres MDCP w ogłoszeniu: snapshoty z chwili publikacji (WGS84). */
export interface MdcpScope {
  points: { number: number; azimuth_deg: number; note: string; lat: number | null; lng: number | null }[];
  buffer: object | null;
}

/**
 * Druga tura widziana przez oferenta.
 *
 * `max_amount_net` to WŁASNA aktualna kwota firmy — sufit, poniżej którego trzeba
 * zejść. Cudzych kwot, ich liczby ani rankingu nie ma tu z założenia: dogrywka jest
 * spokojna, a nie licytacyjna.
 */
export interface RunoffState {
  deadline: string;
  is_open: boolean;
  /** Komunikat kupca do wszystkich zaproszonych. */
  note: string;
  participating: boolean;
  /** Indywidualne dopytanie kupca do TEJ firmy (może być puste). */
  my_note: string;
  first_round_amount_net: string | null;
  max_amount_net: string | null;
  improved: boolean;
}

export interface ProceedingFilters {
  search?: string;
  status?: ProceedingStatus | "";
  access_mode?: AccessMode | "";
  project?: string;
  category?: string;
  mine?: boolean;
  open?: boolean;
  ordering?: string;
}

export function useProceedings(filters: ProceedingFilters = {}) {
  const params: Record<string, string> = {};
  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.access_mode) params.access_mode = filters.access_mode;
  if (filters.project) params.project = filters.project;
  if (filters.category) params.category = filters.category;
  if (filters.mine) params.mine = "1";
  if (filters.open) params.open = "1";
  if (filters.ordering) params.ordering = filters.ordering;

  return useQuery({
    queryKey: ["rfq", "proceedings", params],
    queryFn: () =>
      apiClient
        .get<{ data: Proceeding[] }>("/rfq/proceedings/", { params })
        .then((r) => r.data.data),
  });
}

export interface AudienceRow {
  id: string;
  name: string;
  seen: boolean;
  first_seen_at: string | null;
  last_seen_at: string | null;
  open_count: number;
}

export interface ProceedingPanel {
  proceeding: Proceeding;
  offerer_preview: PublicProceeding;
  audience: AudienceRow[];
  offers: GkOffer[];
  stats: {
    eligible_count: number;
    seen_count: number;
    offers_count: number;
    // null = ogłoszenie niepubliczne, czyli nikt z ulicy nie mógł tu wejść.
    guest_count: number | null;
    guest_open_count: number | null;
  };
  amounts_visible: boolean;
  can_see_sealed_amounts: boolean;
}

export function useProceedingPanel(id: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "proceeding-panel", id],
    enabled: !!id,
    // Oferty i wejścia oferentów przychodzą z zewnątrz (obiekt obiegu) — panel
    // ma zawsze dociągać świeży stan przy wejściu, nie 60-sekundowy cache.
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: () =>
      apiClient
        .get<{ data: ProceedingPanel }>(`/rfq/proceedings/${id}/panel/`)
        .then((r) => r.data.data),
  });
}

export function useSaveProceeding() {
  const qc = useQueryClient();
  return useMutation({
    // Zwraca gotowe postępowanie (rozpakowane z koperty {data,errors,meta}), żeby
    // wywołujący dostał od razu `id` nowego szkicu — potrzebne do dograния załączników.
    mutationFn: ({ id, ...body }: Partial<Proceeding> & { id?: string }) =>
      (id
        ? apiClient.patch<{ data: Proceeding }>(`/rfq/proceedings/${id}/`, body)
        : apiClient.post<{ data: Proceeding }>("/rfq/proceedings/", body)
      ).then((r) => r.data.data),
    // Panel też — edycja szkicu jest dostępna prosto z panelu postępowania.
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", saved.id] });
    },
  });
}

export interface PublishSummary {
  eligible_contractors: number;
  notified_accounts: number;
  mailed_contractors: number;
  mailed_contractor_names: string[];
  contractors_without_account: string[];
}

/* Zasięg zaproszeń MAILOWYCH przy publikacji — wybór kupca na jedno kliknięcie, nie
   właściwość ogłoszenia. Kto ogłoszenie widzi i może złożyć ofertę, wynika wyłącznie
   z trybu dostępu i pozostaje bez zmian. */
export type MailScope = "all" | "selected" | "none";

export interface PublishOptions {
  mail_scope?: MailScope;
  mail_contractors?: string[];
}

/* Co naprawdę wyszło w świat po publikacji — jeden tekst dla obu miejsc, z których
   da się opublikować. Firmy bez konta z dostępem do postępowań wymieniamy z nazwy:
   kupiec ma je dopisać do systemu albo zawiadomić poza nim, a nie zgadywać, dlaczego
   nikt nie wszedł w ogłoszenie. */
export function publishSummaryMessage(meta: PublishSummary): string {
  if (meta.eligible_contractors === 0) {
    return "Opublikowano, ale ŻADNA firma nie spełnia warunków — nikt tego nie zobaczy.";
  }
  const lines = [
    `Opublikowano. Ogłoszenie obejmuje ${meta.eligible_contractors} firm(y).`,
    `Powiadomienia w ELGW 5: ${meta.notified_accounts} kont.`,
  ];
  if (meta.mailed_contractors > 0) {
    // Przy wysyłce imiennej kupiec musi zobaczyć DO KOGO — to jedyny moment, w którym
    // da się jeszcze zareagować, a poczty nie da się cofnąć.
    const names = meta.mailed_contractor_names ?? [];
    lines.push(
      `Zaproszenia mailem: ${meta.mailed_contractors} firm(y)` +
        (names.length > 0 && names.length <= 10 ? ` — ${names.join(", ")}.` : "."),
    );
  } else {
    lines.push("Zaproszenia mailem: nie wysłano żadnego.");
  }
  const missing = meta.contractors_without_account ?? [];
  if (missing.length > 0) {
    lines.push(
      "",
      `UWAGA: ${missing.length} firm(y) nie ma konta z dostępem do postępowań — ` +
        "nie dostaną ani powiadomienia, ani maila:",
      missing.join(", "),
    );
  }
  return lines.join("\n");
}

export function usePublishProceeding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...options }: PublishOptions & { id: string }) =>
      apiClient
        .post<{ meta: PublishSummary }>(`/rfq/proceedings/${id}/publish/`, options)
        .then((r) => r.data.meta),
    onSuccess: (_meta, { id }) => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", id] });
    },
  });
}

export function useDeleteProceeding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/rfq/proceedings/${id}/`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
      // Bez tego cofnięcie się na panel skasowanego szkicu pokazuje go z cache'u.
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", id] });
    },
  });
}

/** Firma na liście wyboru — z oceną, bo to na niej opiera się decyzja o wykluczeniu. */
export interface RatedContractor {
  id: string;
  name: string;
  rating: number | null;
  rating_count: number;
  in_progress_orders: number;
  excluded?: boolean;
}

/** Kto zobaczy ogłoszenie — do sprawdzenia PRZED publikacją (z ocenami). */
export function useEligibleContractors(proceedingId: string | null) {
  return useQuery({
    queryKey: ["rfq", "eligible", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: RatedContractor[] }>(
          `/rfq/proceedings/${proceedingId}/eligible-contractors/`,
        )
        .then((r) => r.data.data),
  });
}

/** Wszystkie firmy z ocenami — źródło pickera wykluczeń w formularzu. */
export function useRatedContractors(enabled = true) {
  return useQuery({
    queryKey: ["rfq", "contractor-ratings"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiClient
        .get<{ data: RatedContractor[] }>("/rfq/contractor-ratings/")
        .then((r) => r.data.data),
  });
}

/* Protokół postępowania (PDF) — pobranie strumieniem, bo endpoint wymaga tokenu,
   więc gołe <a href> by nie przeszło. */
export async function downloadProtocol(proceedingId: string, number: string) {
  const response = await apiClient.get(`/rfq/proceedings/${proceedingId}/protocol/`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `protokol_${number.replace(/\//g, "_")}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type AccountRequestStatus =
  | "new"
  | "contacted"
  | "account_created"
  | "rejected";

export const ACCOUNT_REQUEST_STATUSES: {
  value: AccountRequestStatus;
  label: string;
}[] = [
  { value: "new", label: "Nowa" },
  { value: "contacted", label: "Skontaktowano się" },
  { value: "account_created", label: "Konto założone" },
  { value: "rejected", label: "Odrzucona" },
];

export interface AccountRequest {
  id: string;
  company_name: string;
  nip: string;
  contact_person: string;
  email: string;
  phone: string;
  message: string;
  proceeding: string | null;
  proceeding_number: string | null;
  status: AccountRequestStatus;
  status_label: string;
  note: string;
  handled_by_name: string | null;
  handled_at: string | null;
  /** Dopasowanie po NIP — WYŁĄCZNIE dla nas, nigdy nie wraca do formularza. */
  known_contractor: { id: string; name: string } | null;
  /** Kartoteka założona z tego zgłoszenia — fakt, nie domysł po NIP. */
  linked_contractor: { id: string; name: string } | null;
  source_ip: string | null;
  created_at: string;
}

export function useAccountRequests(status?: AccountRequestStatus | "") {
  return useQuery({
    queryKey: ["rfq", "account-requests", status ?? ""],
    queryFn: () =>
      apiClient
        .get<{ data: AccountRequest[] }>("/rfq/account-requests/", {
          params: status ? { status } : {},
        })
        .then((r) => r.data.data),
  });
}

export function useDecideAccountRequest() {
  const qc = useQueryClient();
  return useMutation({
    // `note` pomijamy, gdy domykamy zgłoszenie z innego ekranu — backend zostawia
    // wtedy notatkę z rozmowy nietkniętą.
    mutationFn: ({
      id,
      status,
      note,
    }: {
      id: string;
      status: AccountRequestStatus;
      note?: string;
    }) =>
      apiClient.post(`/rfq/account-requests/${id}/decide/`, {
        status,
        ...(note === undefined ? {} : { note }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["rfq", "account-requests"] }),
  });
}

/** Zapamiętaj, że z tego zgłoszenia powstała kartoteka firmy. */
export function useLinkContractorToRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contractor }: { id: string; contractor: string }) =>
      apiClient.post(`/rfq/account-requests/${id}/link-contractor/`, {
        contractor,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["rfq", "account-requests"] }),
  });
}

/** Przedsięwzięcia i kupujący do wyboru w formularzu — wspólne słowniki systemu. */
/** Firmy do zaproszenia w trybie „dla wskazanych". */
export function useContractorOptions() {
  return useQuery({
    queryKey: ["contractors", "rfq-picker"],
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; name: string }[] }>("/contractors/")
        .then((r) => r.data.data),
  });
}

export function useProjectOptions() {
  return useQuery({
    queryKey: ["projects", "picker"],
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; name: string }[] }>("/projects/")
        .then((r) => r.data.data),
  });
}

export function useBuyerOptions() {
  return useQuery({
    queryKey: ["users", "rfq-buyers"],
    staleTime: 5 * 60_000,
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; first_name: string; last_name: string; username: string }[] }>(
          "/users/",
        )
        .then((r) => r.data.data),
  });
}

/** Załącznik dochodzi po zapisaniu szkicu — endpoint wisi na istniejącym ogłoszeniu. */
export function useAddAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      proceedingId,
      label,
      file,
    }: {
      proceedingId: string;
      label: string;
      file: File;
    }) => {
      const uploaded = await uploadManagedFile(file);
      return apiClient.post(`/rfq/proceedings/${proceedingId}/attachments/`, {
        managed_file: uploaded.id,
        label,
      });
    },
    onSuccess: (_data, { proceedingId }) => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
    },
  });
}

export function useRemoveAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proceedingId, attachmentId }: { proceedingId: string; attachmentId: string }) =>
      apiClient.delete(`/rfq/proceedings/${proceedingId}/attachments/${attachmentId}/`),
    onSuccess: (_data, { proceedingId }) => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
    },
  });
}

export function useMyProceedingDetail(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["rfq", "my-proceeding", id],
    enabled: enabled && !!id,
    queryFn: () =>
      apiClient
        .get<{ data: PublicProceeding }>(`/rfq/my-proceedings/${id}/`)
        .then((r) => r.data.data),
  });
}

export interface MyProceedings {
  current: PublicProceeding[];
  archived: PublicProceeding[];
}

export function useMyProceedings() {
  return useQuery({
    queryKey: ["rfq", "my-proceedings"],
    queryFn: () =>
      apiClient
        .get<{ data: MyProceedings }>("/rfq/my-proceedings/")
        .then((r) => r.data.data),
  });
}


export type OfferStatus = "submitted" | "withdrawn";

/** Własna oferta oczami oferenta. */
/** Plik dołączony do oferty — `url` bywa null, dopóki antywirus nie skończy skanu. */
export interface OfferAttachment {
  id: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status: ScanStatus;
  created_at: string;
  url: string | null;
}

export interface MyOffer {
  id: string;
  amount_net: string;
  declaration_answers: Record<string, boolean>;
  status: OfferStatus;
  status_label: string;
  submitted_at: string;
  withdrawn_at: string | null;
  binding_until: string;
  /** Kwota z pierwszej tury — ustawiana w chwili ogłoszenia dogrywki. */
  first_round_amount_net: string | null;
  runoff_submitted_at: string | null;
  runoff_note: string;
  attachments: OfferAttachment[];
}

/** Oferta w panelu GK — amount_net i exceeds_max_price bywają null przed otwarciem. */
export interface GkOffer {
  id: string;
  contractor: string;
  contractor_name: string;
  amount_net: string | null;
  exceeds_max_price: boolean | null;
  declaration_answers: Record<string, boolean>;
  status: OfferStatus;
  status_label: string;
  submitted_by_name: string | null;
  submitted_at: string;
  withdrawn_at: string | null;
  binding_until: string;
  rank: number | null;
  /** Kwota sprzed dogrywki — razem z `amount_net` pokazuje, o ile firma zeszła. */
  first_round_amount_net: string | null;
  runoff_submitted_at: string | null;
  /** Indywidualne dopytanie, które kupiec wysłał tej firmie przy dogrywce. */
  runoff_note: string;
  /** Pusta lista przed otwarciem ofert — kosztorys zdradzałby kwotę (patrz backend). */
  attachments: OfferAttachment[];
  /** Ile plików firma dołączyła — leci także przy zamkniętej kopercie. */
  attachment_count: number;
}

export interface OfferPayload {
  amount_net: string;
  declaration_answers: Record<string, boolean>;
  accepted_terms: boolean;
  /** Id plików wgranych wcześniej przez /files/ (uploadAttachment). */
  attachments?: string[];
}

export function useMyOffer(proceedingId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "my-offer", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: MyOffer | null }>(`/rfq/my-proceedings/${proceedingId}/offer/`)
        .then((r) => r.data.data),
  });
}

export function useSubmitOffer(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: OfferPayload) =>
      apiClient.post(`/rfq/my-proceedings/${proceedingId}/offer/`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "my-offer", proceedingId] }),
  });
}

/**
 * Poprawienie kwoty w dogrywce — wyłącznie w dół.
 *
 * Osobny endpoint niż składanie oferty: ankiety oświadczeń i klauzuli nie
 * przeklikuje się drugi raz, zmienia się sama kwota.
 */
export function useSubmitRunoffOffer(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      amount_net,
      attachments = [],
    }: {
      amount_net: string;
      attachments?: string[];
    }) =>
      apiClient.post(`/rfq/my-proceedings/${proceedingId}/runoff-offer/`, {
        amount_net,
        attachments,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "my-offer", proceedingId] });
      qc.invalidateQueries({ queryKey: ["rfq", "my-proceeding", proceedingId] });
      qc.invalidateQueries({ queryKey: ["rfq", "my-proceedings"] });
    },
  });
}

export interface RunoffMailPreview {
  offer_id: string;
  contractor_id: string;
  contractor_name: string;
  kind: string;
  kind_label: string;
  subject: string;
  body: string;
  personal_note: string;
  emails: string[];
  mail_enabled: boolean;
}

export interface RunoffPayload {
  /** Długość tury; domyślnie 24 h. */
  hours: number;
  /** Komunikat wspólny — do wszystkich zaproszonych. */
  note: string;
  /** Dopiski indywidualne: { id firmy: treść }. */
  notes: Record<string, string>;
  reveal_max_price: boolean;
}

/* Podgląd zaproszeń do dogrywki. POST, a nie GET, bo wejściem są komunikaty
   (wspólny + indywidualne) — w adresie by się nie zmieściły. Nic nie zapisuje. */
export function useRunoffPreview(proceedingId: string) {
  return useMutation({
    mutationFn: (payload: RunoffPayload) =>
      apiClient
        .post<{ data: RunoffMailPreview[] }>(
          `/rfq/proceedings/${proceedingId}/runoff-preview/`,
          payload,
        )
        .then((r) => r.data.data),
  });
}

export function useStartRunoff(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RunoffPayload) =>
      apiClient
        .post<{ meta: { invited: number; deadline: string } }>(
          `/rfq/proceedings/${proceedingId}/runoff/`,
          payload,
        )
        .then((r) => r.data.meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
    },
  });
}

export function useWithdrawOffer(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete(`/rfq/my-proceedings/${proceedingId}/offer/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "my-offer", proceedingId] }),
  });
}


export interface DefaultDeclarationQuestion {
  id: string;
  text: string;
  is_active: boolean;
  sort_order: number;
}

export function useDefaultDeclarationQuestions() {
  return useQuery({
    queryKey: ["rfq", "declaration-questions"],
    queryFn: () =>
      apiClient
        .get<{ data: DefaultDeclarationQuestion[] }>("/rfq/declaration-questions/")
        .then((r) => r.data.data),
  });
}

export function useSaveDeclarationQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<DefaultDeclarationQuestion> & { id?: string }) =>
      id
        ? apiClient.patch(`/rfq/declaration-questions/${id}/`, body)
        : apiClient.post("/rfq/declaration-questions/", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "declaration-questions"] }),
  });
}

export function useDeleteDeclarationQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/rfq/declaration-questions/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "declaration-questions"] }),
  });
}


export interface ResolutionMailPreview {
  contractor_id: string;
  contractor_name: string;
  kind: string;
  kind_label: string;
  subject: string;
  body: string;
  emails: string[];
  mail_enabled: boolean;
}

export function useResolutionPreview(
  proceedingId: string,
  action: "award" | "cancel",
  offerId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["rfq", "resolution-preview", proceedingId, action, offerId],
    enabled,
    queryFn: () =>
      apiClient
        .get<{ data: ResolutionMailPreview[] }>(
          `/rfq/proceedings/${proceedingId}/resolution-preview/`,
          { params: { action, ...(offerId ? { offer: offerId } : {}) } },
        )
        .then((r) => r.data.data),
  });
}

export interface ResolvePayload {
  action: "award" | "cancel";
  offer?: string;
  cancel_reason?: string;
  note?: string;
}

export function useResolveProceeding(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ResolvePayload) =>
      apiClient
        .post<{ meta: { notified: number } }>(
          `/rfq/proceedings/${proceedingId}/resolve/`,
          payload,
        )
        .then((r) => r.data.meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceedings"] });
    },
  });
}

/** Pozycja realizacyjna w modalu generowania zamówienia. */
export interface OrderLineItemInput {
  name: string;
  unit: string;
  quantity: string;
  unit_rate: string;
}

export interface GenerateOrderPayload {
  project?: string;
  deadline?: string;
  description?: string;
  line_items: OrderLineItemInput[];
}

/** Wygeneruj Zamówienie ze zwycięskiej oferty; zwraca utworzone zamówienie (z id). */
export function useGenerateOrder(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GenerateOrderPayload) =>
      apiClient
        .post<{ data: { id: string; order_number: string } }>(
          `/rfq/proceedings/${proceedingId}/generate-order/`,
          payload,
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}


export interface Communication {
  id: string;
  title: string;
  body: string;
  is_change: boolean;
  extended_deadline_to: string | null;
  author_name: string | null;
  created_at: string;
  /** Czy oglądające konto oferenta potwierdziło odczyt (dla banera „przeczytane"). */
  read: boolean;
}

export interface GkQuestion {
  id: string;
  contractor: string;
  contractor_name: string;
  body: string;
  answer: string;
  answered_by_name: string | null;
  answered_at: string | null;
  asked_by_name: string | null;
  created_at: string;
  is_public: boolean;
}

export interface MyQuestion {
  id: string;
  body: string;
  answer: string;
  answered_at: string | null;
  created_at: string;
}

/** Publiczna (udostępniona przez GK) para pytanie-odpowiedź — anonimowa. */
export interface PublicQA {
  id: string;
  body: string;
  answer: string;
  answered_at: string | null;
}

export function useCommunications(proceedingId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "communications", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: Communication[] }>(`/rfq/proceedings/${proceedingId}/communications/`)
        .then((r) => r.data.data),
  });
}

export interface CommunicationRecipient {
  contractor_id: string;
  contractor_name: string;
  emails: string[];
  mail_enabled: boolean;
}

export function useCommunicationRecipients(proceedingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["rfq", "comm-recipients", proceedingId],
    enabled: enabled && !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: CommunicationRecipient[] }>(
          `/rfq/proceedings/${proceedingId}/communication-recipients/`,
        )
        .then((r) => r.data.data),
  });
}

export function usePostCommunication(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      body: string;
      is_change: boolean;
      extra_emails: string[];
      to_all_eligible?: boolean;
      new_description?: string;
      new_delivery_deadline?: string;
      new_attachments?: string[];
      attachment_labels?: Record<string, string>;
    }) => apiClient.post(`/rfq/proceedings/${proceedingId}/communications/`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rfq", "communications", proceedingId] });
      qc.invalidateQueries({ queryKey: ["rfq", "proceeding-panel", proceedingId] });
    },
  });
}

/** Pytania widziane przez GK (pełne, z nazwą firmy). */
export function useGkQuestions(proceedingId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "gk-questions", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: GkQuestion[] }>(`/rfq/proceedings/${proceedingId}/questions/`)
        .then((r) => r.data.data),
  });
}

export function useAnswerQuestion(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      answer,
      make_public,
    }: {
      id: string;
      answer: string;
      make_public?: boolean;
    }) => apiClient.post(`/rfq/questions/${id}/answer/`, { answer, make_public }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "gk-questions", proceedingId] }),
  });
}

/** Publiczne (anonimowe) pary pytanie-odpowiedź — dla oferenta. */
export function usePublicQA(proceedingId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "public-qa", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: PublicQA[] }>(`/rfq/my-proceedings/${proceedingId}/public-qa/`)
        .then((r) => r.data.data),
  });
}

/** Oferent potwierdza odczyt komunikatu. */
export function useMarkCommunicationRead(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (communicationId: string) =>
      apiClient.post(`/rfq/communications/${communicationId}/mark-read/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["rfq", "communications", proceedingId] }),
  });
}

/** Własne pytania oferenta. */
export function useMyQuestions(proceedingId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "my-questions", proceedingId],
    enabled: !!proceedingId,
    queryFn: () =>
      apiClient
        .get<{ data: MyQuestion[] }>(`/rfq/my-proceedings/${proceedingId}/questions/`)
        .then((r) => r.data.data),
  });
}

export function useAskQuestion(proceedingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      apiClient.post(`/rfq/my-proceedings/${proceedingId}/questions/`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rfq", "my-questions", proceedingId] }),
  });
}


export interface CommunicationMailRow {
  id: string;
  contractor_name: string | null;
  to: string[];
  status: string;
  status_label: string;
  sent_at: string | null;
  error: string;
}

export interface CommunicationReadRow {
  user_name: string;
  contractor_name: string | null;
  read_at: string | null;
}

export interface CommunicationLog {
  rows: CommunicationMailRow[];
  reads: CommunicationReadRow[];
  read_count: number;
  expected_count: number;
}

export function useCommunicationMailLog(communicationId: string | null) {
  return useQuery({
    queryKey: ["rfq", "comm-mail-log", communicationId],
    enabled: !!communicationId,
    queryFn: () =>
      apiClient
        .get<{
          data: CommunicationMailRow[];
          meta: {
            reads: CommunicationReadRow[];
            read_count: number;
            expected_count: number;
          };
        }>(`/rfq/communications/${communicationId}/mail-log/`)
        .then(
          (r): CommunicationLog => ({
            rows: r.data.data,
            reads: r.data.meta.reads,
            read_count: r.data.meta.read_count,
            expected_count: r.data.meta.expected_count,
          }),
        ),
  });
}
